import { useState, useEffect } from 'react';

export default function Hero() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-[#612c42] via-[#7a3952] to-[#612c42]">
      {/* Decorative Elements */}
      <div className="absolute top-20 right-20 w-64 h-64 bg-[#c47a3a]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-20 w-96 h-96 bg-[#059669]/10 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-6 pt-32 pb-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-white">
            <div className="inline-block mb-6">
              <span className="px-4 py-2 bg-[#c47a3a]/20 backdrop-blur-sm rounded-full text-[#e8a96a] text-sm font-medium border border-[#c47a3a]/30">
                Est. 1993 • Trusted Excellence
              </span>
            </div>

            <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight" style={{ fontFamily: "'EB Garamond', 'Times New Roman', serif" }}>
              Precision Accounting.
              <span className="block text-[#c47a3a]">Personal Service.</span>
            </h1>

            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Butler Professional Services provides accurate, timely bookkeeping and accounting support for small businesses, nonprofits, real estate professionals, and insurance agencies.
            </p>

            {/* Mobile Image */}
            <div className="lg:hidden mb-8">
              <div className="relative max-w-sm mx-auto">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#c47a3a]/20 to-[#059669]/10 rounded-2xl blur-xl"></div>
                <div className="relative bg-white/10 backdrop-blur-sm rounded-2xl p-2 border border-white/20">
                  <div className="w-full h-[400px] rounded-xl overflow-hidden">
                    <img
                      src="https://static.readdy.ai/image/7f87cd0470b0952245d57f0e6ea3c4d4/0a1af3a608eee16e0eb974cbfa9f5bb0.jpeg"
                      alt="Antonia Mayweather - Owner of Butler Professional Services"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-2xl p-4 border-4 border-[#c47a3a]">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-[#612c42]" style={{ fontFamily: "'EB Garamond', serif" }}>30+</div>
                    <div className="text-xs text-gray-600 font-medium">Years Experience</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Key Points */}
            <div className="space-y-4 mb-10">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center mt-1">
                  <i className="ri-checkbox-circle-fill text-[#059669] text-xl"></i>
                </div>
                <p className="text-white/90">Over 30 years of accounting and bookkeeping experience</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center mt-1">
                  <i className="ri-checkbox-circle-fill text-[#059669] text-xl"></i>
                </div>
                <p className="text-white/90">Specializing in real estate, insurance, and nonprofit accounting</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center mt-1">
                  <i className="ri-checkbox-circle-fill text-[#059669] text-xl"></i>
                </div>
                <p className="text-white/90">Streamlined, cloud-based bookkeeping with QuickBooks Online and other leading platforms</p>
              </div>
            </div>

            {/* CTA */}
            <div className="flex flex-wrap gap-4">
              <div className="relative group">
                <button
                  disabled
                  className="px-8 py-4 bg-[#c47a3a]/60 text-white/70 font-semibold rounded-lg cursor-not-allowed whitespace-nowrap shadow-lg"
                >
                  Request a Consultation
                </button>
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-gray-900 text-white text-xs font-semibold px-3 py-1.5 rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                  Coming Soon
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-gray-900"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Image - Desktop Only */}
          <div className="relative lg:block hidden">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-[#c47a3a]/20 to-[#059669]/10 rounded-2xl blur-xl"></div>
              <div className="relative bg-white/10 backdrop-blur-sm rounded-2xl p-2 border border-white/20">
                <div className="w-full h-[600px] rounded-xl overflow-hidden">
                  <img
                    src="https://static.readdy.ai/image/7f87cd0470b0952245d57f0e6ea3c4d4/0a1af3a608eee16e0eb974cbfa9f5bb0.jpeg"
                    alt="Antonia Mayweather - Owner of Butler Professional Services"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-2xl p-6 border-4 border-[#c47a3a]">
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#612c42]" style={{ fontFamily: "'EB Garamond', serif" }}>30+</div>
                  <div className="text-sm text-gray-600 font-medium">Years Experience</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
